import { Avatar, Button, Popover, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import type { Environment } from '@prisma/client';
import { IconAlertTriangleFilled, IconCircleCheck, IconClockFilled } from '@tabler/icons-react';
import { useAtom, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useRouter } from 'next/router';
import { useCallback, useRef, useState } from 'react';
import { match } from 'ts-pattern';
import { aiChatLogsAtomFamily } from '~/atoms/ai-chat-atoms';
import { prevStepKeysAtom, prevStepStoredInTempFamily, previewExecutionIdAtom } from '~/atoms/atoms';
import { erroredOperationAtom } from '~/atoms/no-code-atoms';
import {
  autoHealStepAtom,
  completedItemsAtom,
  conditionalBlockResultsAtom,
  executedVariablesAtom,
  fastAutoHealAtom,
  recorderConsoleLogsAtom,
  recorderNetworkLogsAtom,
  recorderPausedAtom,
  timeoutAtom,
} from '~/atoms/recorder-atoms';
import { ERROR } from '~/common/recorder/constants';
import type { RecorderCommand } from '~/common/recorder/recorder-operation';
import { AI_HUMAN_HELP_SIGNAL, VM_TIMEOUT_MS } from '~/common/vm/constants';
import type { VmServerToClientMessage } from '~/common/vm/protocol';
import { type TabUpdateMessage, type VmFillInputPayload, type VmSelectOptionsPayload } from '~/common/vm/protocol';
import type { RecorderExecutionType } from '~/common/vm/shared-types';
import { getBrowserModalProps } from '~/components/browser/Browser';
import URLEntry from '~/components/forms/URLEntry';
import type { RecorderInitiatorLocation } from '~/server/logic/recorder/recorder-types';
import { api } from '~/trpc-extra/api';
import { useAuthData } from '../auth/use-auth-data';
import { useTimerCounter } from '../time/useTimeCounter';
import { useLiveStateManager } from '../useLiveStateManager';
import { useRecorderClient } from './recorder-client/use-recorder-client';
import { useResetRecorderAtoms } from './useResetRecorder';
import { useSaveAiProgress } from './useSaveAiProgress';

type StatusState =
  | 'loading'
  | 'ready'
  | 'error'
  | 'warning'
  | 'closing'
  | 'closed'
  | 'connected'
  | 'replaying'
  | 'finished'
  | 'select-options'
  | 'fill-input'
  | 'record-action';

export type VmStatus = {
  state: StatusState;
  // Extra info to accompany state for better display purposes / human consumption
  humanState: StatusState;
  humanDetails?: string;
  aliasesToUrls?: Record<string, string>;
  executionState?: {
    // Usually this is the operation that is being executed
    // But for AI steps, it is the ID of the step (it's a hack)
    operationId?: string;
    // If this step comes from a component and the component is being used in an operation, this will be that operation id
    stepSourceOperationId?: string;
    // The step that is currently being executed
    currentStepId?: string;
  };
};

export const useVm = ({
  testContentId,
  // TODO: refactor this to potentially remove the executionType param.
  // It can change in the middle of the test so it's not good for other handler code to depend on it
  executionType,
  startEpoch,
  onTabUpdate,
}: {
  testContentId: string;
  executionType: RecorderExecutionType;
  startEpoch?: number;
  onTabUpdate?: (update: TabUpdateMessage) => void;
}) => {
  const { isAdmin } = useAuthData();
  const trpcUtils = api.useUtils();
  const saveAiProgress = useSaveAiProgress({ testContentId });
  const router = useRouter();

  const isTestDraftView = router.pathname.includes('/testDraft/');

  const setConsoleLogs = useSetAtom(recorderConsoleLogsAtom);
  const setNetworkLogs = useSetAtom(recorderNetworkLogsAtom);
  const setAILogs = useSetAtom(aiChatLogsAtomFamily(testContentId));
  const resetRecorderAtoms = useResetRecorderAtoms(testContentId);
  const setAutoHealStep = useSetAtom(autoHealStepAtom);
  const setTimeoutInfo = useSetAtom(timeoutAtom);
  const setFastAutoHeal = useSetAtom(fastAutoHealAtom);
  const [erroredOperation, setErroredOperation] = useAtom(erroredOperationAtom);
  const setCompletedItems = useSetAtom(completedItemsAtom);
  const setPreviewExecutionId = useSetAtom(previewExecutionIdAtom);
  const setConditionalBlockResults = useSetAtom(conditionalBlockResultsAtom);

  const { mutateAsync: digestError } = api.operation.digestError.useMutation();

  const activeTabRef = useRef('page');

  const [fileDialogIsOpen, setFileDialogOpen] = useState<boolean>(false);
  const { vmStatus, recorderState, updateLiveState } = useLiveStateManager();
  const setRecorderPaused = useSetAtom(recorderPausedAtom);

  const setVariables = useSetAtom(executedVariablesAtom);
  // Adding epoch as uniqueness to trigger re-renders in consumers
  const [fillInputData, setFillInputData] = useState<VmFillInputPayload & { epoch: number }>();
  // Adding epoch as uniqueness to trigger re-renders in consumers
  const [selectOptionsPayload, setSelectOptionsPayload] = useState<VmSelectOptionsPayload & { epoch: number }>();

  const { mutateAsync: addStep } = api.testStep.insert.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Step created', color: 'blue' });
    },
    onError: () => {
      notifications.show({ message: 'Error creating step', color: 'red' });
    },
  });

  const handleContinueRecording = async ({
    stepId,
    stepOrder,
    operation,
    errorMessage,
    aliasesToUrls,
    aborted,
    resumeFromCursor,
    sendServerCommand,
  }: {
    stepId: string;
    stepOrder: number;
    operation?: { id: string; order: number };
    errorMessage: string;
    aliasesToUrls: Record<string, string>;
    aborted: boolean;
    resumeFromCursor: boolean;
    sendServerCommand: (command: RecorderCommand) => void;
  }) => {
    const currentSteps = trpcUtils.testContent.get.getData({ id: testContentId })?.steps ?? [];
    const currentErrorStep = currentSteps.find((s) => s.id === stepId);
    const errorStepIsValid = currentErrorStep?.mode === 'AI_FALLBACK' || currentErrorStep?.mode === 'STRICT';

    const errorStepCursorLocation =
      errorStepIsValid ?
        {
          stepId,
          stepOrder,
          operation:
            (aborted || resumeFromCursor) && operation ? { id: operation.id, order: operation.order } : undefined,
        }
      : await (async () => {
          const newStep = await addStep({
            description: '',
            order: stepOrder + 1,
            testContentId,
            mode: 'AI_FALLBACK',
          });
          return {
            stepId: newStep.id,
            stepOrder: newStep.stepOrder,
            operation: undefined,
          };
        })();

    if (executionType === 'preview') {
      // Let's just ignore all interaction warnings from now on, you already had an error
      sendServerCommand({
        type: 'switch-mode',
        mode: 'record',
        cursorStepId: errorStepCursorLocation.stepId,
        cursorStepOrder: errorStepCursorLocation.stepOrder,
        cursorOperation: errorStepCursorLocation.operation,
      });
      updateLiveState((prev) => ({
        ...prev,
        vmStatus: {
          state: 'ready',
          humanState: 'ready',
          humanDetails: errorMessage,
          aliasesToUrls,
        },
        recorderInitiator: { ...prev.recorderInitiator, executionType: 'record' },
      }));
    } else {
      updateLiveState((prev) => ({
        ...prev,
        vmStatus: {
          state: 'ready',
          humanState: 'ready',
          aliasesToUrls,
        },
      }));
    }
    modals.closeAll();
    // Ok to call saveAiProgress after the modal close. That way the UI looks snappier and
    // it's unlikely we run into any serious race condition
    await saveAiProgress(stepId);
  };

  const updatePrevStepStoredInTemp = useAtomCallback(
    useCallback((get, set, params: { stepId: string; executionId: string; value: boolean }) => {
      const key = `${params.stepId}_${params.executionId}`;
      // Set the value in the atom family.
      set(prevStepStoredInTempFamily(key), params.value);
      // Also add this key to the keys tracker.
      set(prevStepKeysAtom, (prevKeys) => {
        if (!prevKeys.includes(key)) {
          return [...prevKeys, key];
        }
        return prevKeys;
      });
    }, []),
  );

  const handleMessage = useCallback(
    async ({
      message,
      close,
      sendServerCommand,
    }: {
      message: VmServerToClientMessage;
      close: () => void;
      sendServerCommand: (message: RecorderCommand) => void;
    }) => {
      await match(message)
        .with({ type: 'ai-message' }, (message) => {
          setAILogs({ type: 'appendMessageToCurrentLog', message: message.data });
        })
        .with({ type: 'ai-prev-step-stored-in-temp' }, (message) => {
          updatePrevStepStoredInTemp({
            stepId: message.stepId,
            executionId: message.previewExecutionId,
            value: true,
          });
        })
        .with({ type: 'ai-step-started' }, (message) => {
          updateLiveState((prev) => ({
            ...prev,
            vmStatus:
              prev.vmStatus ?
                {
                  ...prev.vmStatus,
                  state: 'replaying',
                  humanState: 'replaying',
                  executionState: {
                    ...prev.vmStatus?.executionState,
                    operationId: message.stepId,
                    currentStepId: message.stepId,
                  },
                }
              : undefined,
          }));
          updatePrevStepStoredInTemp({
            stepId: message.stepId,
            executionId: message.previewExecutionId,
            value: false,
          });
        })
        .with({ type: 'autoheal-step' }, (message) => {
          const { stepId } = message;
          // Set the atom for displaying auto-heal
          setAutoHealStep(stepId);
        })
        .with({ type: 'timeout-update' }, ({ update }) => {
          match(update)
            .with({ type: 'start' }, ({ stepId, totalTimeoutMs }) => {
              setTimeoutInfo({ stepId, totalTimeoutMs });
            })
            .with({ type: 'hide' }, () => {
              setTimeoutInfo(undefined);
            })
            .exhaustive();
        })
        .with({ type: 'ai-fast-auto-heal' }, ({ stepId, operationId }) => {
          setFastAutoHeal((prev) => {
            // Check if this operation is already in the array to avoid duplicates
            const exists = prev.some((item) => item.operationId === operationId);
            return exists ? prev : [...prev, { stepId, operationId }];
          });
        })
        .with({ type: 'browser-log' }, (message) => {
          setConsoleLogs((prev) => [...prev, { ...message, addedAtEpochMs: Date.now() }]);
        })
        .with({ type: 'completed-items-update' }, ({ update }) => {
          setCompletedItems(update);
        })
        .with({ type: 'cursor-update' }, () => {
          // updateLiveState((prev) => ({
          //   ...prev,
          //   recorderCursor: message.recorderCursor,
          // }));
        })
        .with({ type: 'conditional-evaluation-result' }, (message) => {
          // TODO: consider using a set with conditionalBlockId + resolvedTo instead of a map - not sure which is better for atoms
          setConditionalBlockResults((prev) => ({
            ...prev,
            [message.conditionalBlockId]: message.resolvedTo,
          }));
          // eslint-disable-next-line react-hooks/exhaustive-deps
        })
        .with({ type: 'execution-error' }, async (message) => {
          const { error, step, aborted, resumeFromCursor, variables } = message;

          setVariables(variables);

          const humanHelp = error.includes(AI_HUMAN_HELP_SIGNAL);
          const errorTitle = humanHelp ? 'Human Help Required' : 'Test Error';
          const errorMessage = humanHelp ? error : await digestError({ error });
          const rawErrorMessage = error;

          // "Cancelled" executions is not an error, so we only set the errored operation if it's not aborted
          if (!aborted) {
            // Note: we filter out sensitive implementation details from the error message just to improve UX
            // It is not really "secure" as the error message is still visible to the user through AI logs
            // Preventing sensitive info leak still requires other mechanisms that is more reliable
            setErroredOperation({ id: message.operation.id, errorText: errorMessage });
          }

          modals.open({
            withinPortal: false,
            ...getBrowserModalProps(startEpoch),
            centered: true,
            closeOnClickOutside: false,
            closeButtonProps: {
              onClick: async () => {
                // Close the modal first to make the UI look snappier
                modals.closeAll();
                await saveAiProgress(step.id);
                await resetRecorderAtoms();
                close();
              },
            },
            children: (
              <div className="flex flex-col items-center px-6 pb-8">
                <Avatar
                  className="mb-4"
                  color="red"
                  variant="light"
                  radius="xl"
                  size={50}
                >
                  <IconAlertTriangleFilled size={32} />
                </Avatar>

                <Text className="mb-4 text-center font-medium">{aborted ? 'Execution cancelled' : errorTitle}</Text>
                {!aborted && (
                  <div className="mb-4 rounded-md border border-[var(--mantine-color-default-border)] bg-gray-100 p-2 font-mono dark:bg-neutral-900">
                    <Text size="sm">{errorMessage}</Text>
                    {!!isAdmin && (
                      <Text
                        size="sm"
                        c="orange"
                        className="mt-2"
                      >
                        {rawErrorMessage}
                      </Text>
                    )}
                  </div>
                )}
                <div className="flex w-full flex-col gap-2">
                  {!!isTestDraftView && (
                    <Button
                      title="Continue recording"
                      variant="filled"
                      radius="md"
                      color="blue"
                      onClick={() =>
                        handleContinueRecording({
                          errorMessage: error,
                          stepId: step.id,
                          stepOrder: step.order,
                          operation: { id: message.operation.id, order: message.operation.order || 0 },
                          aliasesToUrls: message.aliasesToUrls,
                          aborted,
                          resumeFromCursor,
                          sendServerCommand,
                        })
                      }
                    >
                      <div className="flex items-center gap-2">Continue Recording</div>
                    </Button>
                  )}
                  <Button
                    title="Close browser"
                    variant="subtle"
                    radius="md"
                    color="gray"
                    onClick={async () => {
                      modals.closeAll();
                      await saveAiProgress(step.id);
                      await resetRecorderAtoms();
                      close();
                    }}
                  >
                    Close Browser
                  </Button>
                </div>
              </div>
            ),
          });
        })
        .with({ type: 'execution-type-change' }, (message) => {
          if (message.executionType === 'record') {
            // Replay can be aborted before timeout "hide" propagates; force-clear countdown in record mode.
            setTimeoutInfo(undefined);
            // Execution type has changed. So must extract from the message and shouldn't use the one from the VM.
            updateLiveState((prev) => ({
              vmStatus: {
                state: 'ready',
                humanState: 'ready',
                aliasesToUrls: message.aliasesToUrls,
                executionState: {
                  operationId: message.newRecordCursor.operation?.id,
                  currentStepId: message.newRecordCursor.stepId,
                },
              },
              recorderCursor: message.newRecordCursor,
              recorderInitiator: { ...prev.recorderInitiator, executionType: message.executionType },
            }));
          } else if (message.executionType === 'preview') {
            updateLiveState((prev) => ({
              ...prev,
              vmStatus: {
                state: 'replaying',
                humanState: 'replaying',
                aliasesToUrls: message.aliasesToUrls,
                executionState: prev.vmStatus?.executionState,
              },
              recorderInitiator: { ...prev.recorderInitiator, executionType: message.executionType },
            }));
          }
          setVariables(message.variables);
        })
        .with({ type: 'fast-forward' }, (message) => {
          if (message.operationId === erroredOperation?.id) {
            setErroredOperation(undefined);
          }

          updateLiveState((prev) => ({
            ...prev,
            // Keep the recording insertion cursor stable during replay.
            // Replay progress should only affect executionState/highlighting.
            recorderCursor: prev.recorderCursor,
            vmStatus: {
              state: 'replaying',
              humanState: 'replaying',
              humanDetails: message.step.description,
              aliasesToUrls: message.aliasesToUrls,
              executionState: {
                operationId: message.operationId,
                stepSourceOperationId: message.step.sourceOperationId,
                currentStepId: message.step.id,
              },
            },
          }));
          setVariables(message.variables);
        })
        .with({ type: 'file-upload' }, () => {
          setFileDialogOpen(true);
        })
        .with({ type: 'fill-input' }, (message) => {
          setFillInputData({ data: message.data, target: message.target, epoch: Date.now() });
          updateLiveState((prev) => ({
            ...prev,
            vmStatus: { ...prev.vmStatus, state: 'ready', humanState: 'ready', humanDetails: 'Fill input' },
          }));
        })
        .with({ type: 'log' }, ({ msg }) => {
          console.log('VM-Log: ', msg);
        })
        .with({ type: 'network-log' }, (message) => {
          setNetworkLogs((prev) => {
            const existingIndex = prev.findIndex((log) => log.requestId === message.requestId);

            // Once the response is received, replace the request log with the fully populated response log
            return existingIndex !== -1 ?
                [...prev.slice(0, existingIndex), message, ...prev.slice(existingIndex + 1)]
              : [...prev, message];
          });
        })
        .with({ type: 'preview-execution-started' }, (message) => {
          setPreviewExecutionId(message.previewExecutionId);
        })
        .with({ type: 'preview-success' }, (message) => {
          setVariables(message.variables);
          updateLiveState((prev) => ({
            ...prev,
            vmStatus: { ...prev.vmStatus, state: 'finished', humanState: 'finished' },
            // Sets the cursor to the last step of the preview execution
            recorderCursor:
              message.operation ?
                {
                  stepId: message.stepId,
                  stepOrder: message.stepOrder,
                  operation: {
                    id: message.operation.id,
                    order: message.operation.order,
                  },
                }
              : {
                  stepId: message.stepId,
                  stepOrder: message.stepOrder,
                },
          }));
          const handleContinue = () => {
            sendServerCommand({
              type: 'switch-mode',
              mode: 'record',
              cursorStepId: message.stepId,
              cursorStepOrder: message.stepOrder,
              cursorOperation: message.resumeFromCursor ? message.operation : undefined,
            });
            updateLiveState((prev) => ({
              ...prev,
              vmStatus: { ...prev.vmStatus, state: 'record-action', humanState: 'record-action' },
              recorderCursor:
                message.resumeFromCursor ?
                  {
                    stepId: message.stepId,
                    stepOrder: message.stepOrder,
                    operation: message.operation,
                  }
                : undefined,
            }));
            modals.closeAll();
          };
          const handleClose = async () => {
            // Close the modal first to make the UI look snappier
            modals.closeAll();
            await resetRecorderAtoms();
            close();
          };
          modals.open({
            withinPortal: false,
            ...getBrowserModalProps(startEpoch),
            centered: true,
            closeOnClickOutside: false,
            size: 'sm',
            closeButtonProps: {
              onClick: async () => (isTestDraftView ? handleContinue() : await handleClose()),
            },
            children: (
              <div className="flex flex-col items-center px-6 pb-8">
                <Avatar
                  className="mb-4"
                  color="green"
                  variant="light"
                  radius="xl"
                  size={50}
                >
                  <IconCircleCheck size={32} />
                </Avatar>

                <Text className="mb-4 text-center font-medium">Test Preview Success!</Text>
                <div className="flex w-full flex-col gap-2">
                  {!!isTestDraftView && (
                    <Button
                      title="Continue recording"
                      variant="filled"
                      radius="md"
                      color="blue"
                      onClick={handleContinue}
                    >
                      <div className="flex items-center gap-2">Continue Recording</div>
                    </Button>
                  )}
                  <Button
                    title="Close browser"
                    variant="subtle"
                    radius="md"
                    color="gray"
                    onClick={handleClose}
                  >
                    Close Browser
                  </Button>
                </div>
              </div>
            ),
          });
        })
        .with({ type: 'prompt' }, (message) => {
          const clientPromptResp = window.prompt(message.dialogMessage) || '';
          sendServerCommand({ type: 'prompt', promptValue: clientPromptResp });
        })
        .with({ type: 'recorded' }, async ({ recorderCursor, aliasesToUrls, variables }) => {
          await Promise.allSettled([
            trpcUtils.testContent.get.invalidate({ id: testContentId }),
            trpcUtils.testContent.getCode.invalidate({ testContentId }),
            // If recording into a parent op, also invalidate the parents query (component or conditional)
            recorderCursor.parentContext?.componentId &&
              trpcUtils.component.get.invalidate({ id: recorderCursor.parentContext.componentId }),
            recorderCursor.parentContext?.conditional &&
              trpcUtils.operation.getFullConditionalBlock.invalidate({
                conditionalBlockId: recorderCursor.parentContext.conditional.conditionalBlockId,
              }),
          ]);
          updateLiveState((prev) => ({
            ...prev,
            vmStatus: {
              state: 'ready',
              humanState: 'ready',
              aliasesToUrls,
            },
            recorderCursor,
          }));
          setVariables(variables);
        })
        .with({ type: 'record-action' }, (message) => {
          const { action, target, value } = message;
          sendServerCommand({
            type: 'operation',
            operation: {
              operationType: 'ACTION',
              target,
              value,
              valueType: 'STATIC',
              action,
            },
          });
        })
        .with({ type: 'recording' }, (_) => {})
        .with({ type: 'recorder-pause-change' }, (message) => {
          setRecorderPaused(message.paused);
        })
        .with({ type: 'refresh-code' }, async () => {
          await Promise.allSettled([
            trpcUtils.testContent.get.invalidate({ id: testContentId }),
            trpcUtils.testContent.getCode.invalidate({ testContentId }),
            // If currently recording into a parent op, also invalidate the conditional/component query
            recorderState?.parentContext?.componentId &&
              trpcUtils.component.get.invalidate({ id: recorderState.parentContext.componentId }),
            recorderState?.parentContext?.conditional &&
              trpcUtils.operation.getFullConditionalBlock.invalidate({
                conditionalBlockId: recorderState.parentContext.conditional.conditionalBlockId,
              }),
          ]);
        })
        .with({ type: 'select-options' }, (message) => {
          setSelectOptionsPayload({ options: message.options, target: message.target, epoch: Date.now() });
          updateLiveState((prev) => ({
            ...prev,
            vmStatus: { ...prev.vmStatus, state: 'ready', humanState: 'ready', humanDetails: 'Select options' },
          }));
        })
        .with({ type: 'tab-syncing-urls' }, (message) => {
          updateLiveState((prev) => ({
            ...prev,
            vmStatus:
              prev.vmStatus ?
                {
                  ...prev.vmStatus,
                  aliasesToUrls: message.aliasesToUrls ?? prev.vmStatus.aliasesToUrls,
                }
              : undefined,
          }));
          // TODO: use consistent message name
        })
        .with({ type: 'tab-update' }, (message) => {
          onTabUpdate?.(message);
        })
        .with({ type: 'waiting-user-input' }, (message) => {
          updateLiveState((prev) => ({
            ...prev,
            vmStatus: {
              // Preserve execution context for errored step & op UI
              executionState: prev.vmStatus?.executionState,
              state: 'ready',
              humanState: 'ready',
              humanDetails: message.message,
              aliasesToUrls: message.aliasesToUrls,
            },
          }));
          setVariables(message.variables);
        })
        .with({ type: 'warning' }, (message) => {
          modals.open({
            withinPortal: false,
            ...getBrowserModalProps(startEpoch),
            centered: true,
            children: (
              <div className="flex flex-col items-center gap-4">
                <IconAlertTriangleFilled
                  className="text-yellow-400"
                  size={48}
                />
                {message.message.includes(ERROR.UNABLE_TO_RECORD_ACTION) ?
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-center text-lg">We couldn&apos;t capture that click</div>
                    <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                      Please try clicking again, but a bit more slowly this time.
                      <Popover
                        width={300}
                        position="bottom"
                        withArrow
                        shadow="md"
                      >
                        <Popover.Target>
                          <div className="text-gray-600 underline hover:cursor-pointer dark:text-gray-400">
                            Learn more
                          </div>
                        </Popover.Target>
                        <Popover.Dropdown>
                          <Text size="sm">
                            Our virtual browser needs a moment to process each action and refresh.
                            <br />
                            <br />
                            When clicking too quickly, the browser may not have time to refresh between actions, causing
                            some clicks to be missed.
                            <br />
                            <br />
                            Try pausing briefly between clicks for the best results.
                          </Text>
                        </Popover.Dropdown>
                      </Popover>
                    </div>
                  </div>
                : message.message.includes(ERROR.DISABLED_ELEMENT) ?
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-center text-lg">This element cannot be interacted with</div>
                    <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                      The element you tried to use is currently in a disabled state.
                    </div>
                  </div>
                : <Text className="text-center">{message.message || 'Warning'}</Text>}
                <Button
                  fullWidth
                  variant="subtle"
                  className="mt-4 rounded-md px-10"
                  onClick={async () => {
                    modals.closeAll();
                    updateLiveState((prev) => ({
                      ...prev,
                      vmStatus: {
                        state: 'ready',
                        humanState: 'ready',
                        aliasesToUrls: message.aliasesToUrls,
                      },
                    }));
                  }}
                >
                  Continue
                </Button>
              </div>
            ),
          });
        })
        .exhaustive();
    },
    [
      executionType,
      updateLiveState,
      startEpoch,
      trpcUtils.testContent.getCode,
      trpcUtils.component.get,
      trpcUtils.operation.getFullConditionalBlock,
      updatePrevStepStoredInTemp,
      recorderState?.parentContext,
    ],
  );
  const recorderClient = useRecorderClient({
    testContentId,
    executionType,
    messageHandler: handleMessage,
  });

  const close = useCallback(
    // TODO: rename this variable to skipSetVMStatus
    async (options?: { skipSetStatus?: boolean }) => {
      setVariables([]);
      if (!options?.skipSetStatus) {
        updateLiveState((prev) => ({
          ...prev,
          vmStatus: { ...prev.vmStatus, state: 'closing', humanState: 'closing' },
          recorderCursor: undefined,
          recorderInitiator: undefined,
        }));
      }
      try {
        recorderClient.send({ type: 'terminate' });
      } catch (e) {
        // ignore
      }
      try {
        await recorderClient.close();
      } catch (e) {
        // ignore
      }
      if (!options?.skipSetStatus) {
        updateLiveState((prev) => ({
          ...prev,
          vmStatus: { ...prev.vmStatus, state: 'closed', humanState: 'closed' },
          recorderCursor: undefined,
          recorderInitiator: undefined,
        }));
      }
    },
    [updateLiveState, recorderClient.send, recorderClient.close],
  );

  const timeRemaining = useTimerCounter(new Date(Date.now() + VM_TIMEOUT_MS), {
    onNegative: () => {
      console.log('Out of VM time');
      modals.open({
        withinPortal: false,
        ...getBrowserModalProps(startEpoch),
        centered: true,
        closeOnClickOutside: false,
        withCloseButton: false,
        children: (
          <div className="flex flex-col items-center px-6 pb-8">
            <Avatar
              className="mb-4"
              color="orange"
              variant="light"
              radius="xl"
              size={50}
            >
              <IconClockFilled size={32} />
            </Avatar>
            <Text className="mb-4 text-center font-medium">Session Timed Out</Text>
            <Text className="mb-4 text-center text-sm text-gray-600 dark:text-gray-400">
              Your VM session was closed due to inactivity.
            </Text>
            <Button
              title="Close"
              variant="filled"
              radius="md"
              color="blue"
              fullWidth
              onClick={() => {
                modals.closeAll();
                close().catch(() => undefined);
              }}
            >
              Close
            </Button>
          </div>
        ),
      });
    },
  });

  const startVm = useCallback(
    async ({
      recorderInitiatorLocation,
      antiBot,
      useProxy,
      environment,
    }: {
      recorderInitiatorLocation?: RecorderInitiatorLocation;
      antiBot?: boolean;
      useProxy?: boolean;
      environment?: Environment;
    }) => {
      updateLiveState((prev) => ({
        vmStatus: { ...prev.vmStatus, state: 'loading', humanState: 'loading', humanDetails: 'Initializing' },
        recorderCursor:
          recorderInitiatorLocation ?
            {
              ...recorderInitiatorLocation,
              stepOrder: recorderInitiatorLocation.order,
              operation:
                recorderInitiatorLocation.type === 'operation-id' && recorderInitiatorLocation.operationId ?
                  {
                    id: recorderInitiatorLocation.operationId,
                    order: recorderInitiatorLocation.operationOrder,
                  }
                : undefined,
            }
          : undefined,
        recorderInitiator: {
          location: recorderInitiatorLocation,
          executionType,
        },
      }));
      try {
        await recorderClient.start({
          recorderInitiatorLocation,
          antiBot,
          useProxy,
          environment,
        });
      } catch (e) {
        console.error('Error starting VM:', e instanceof Error ? e.message : JSON.stringify(e));
        updateLiveState((prev) => ({
          vmStatus: { ...prev.vmStatus, state: 'error', humanState: 'error', humanDetails: 'Error initializing VM' },
          recorderCursor: undefined,
          recorderInitiator: undefined,
        }));
        throw e;
      }
    },
    [updateLiveState],
  );

  const sendCommand = (command: RecorderCommand) => {
    // Intercept new tab navigations
    if (command.type === 'operation' && command.operation.operationType === 'NAVIGATION' && command.operation.newPage) {
      modals.open({
        withinPortal: false,
        ...getBrowserModalProps(startEpoch),
        centered: true,
        title: 'Open new tab',
        children: (
          <URLEntry
            onSubmit={(url) => {
              recorderClient.send({
                type: 'operation',
                operation: { operationType: 'NAVIGATION', value: url, newPage: true },
                pageAlias: activeTabRef.current,
              });
              modals.closeAll();
            }}
          />
        ),
      });
      return;
    }
    if (command.type === 'tab-switch') {
      activeTabRef.current = command.pageAlias;
    } else if (command.type === 'file-upload') {
      setFileDialogOpen(false);
    }

    const commandWithPageAlias = { ...command, pageAlias: activeTabRef.current };

    try {
      recorderClient.send(commandWithPageAlias);
    } catch (e) {
      console.error('Unable to send message: ', e);
    }
  };

  return {
    status: vmStatus,
    activeTab: activeTabRef.current,
    mediaSource: recorderClient.mediaSource,
    sendCommand,
    timeRemaining,
    fileDialogIsOpen,
    close,
    startVm,
    selectOptionsPayload,
    fillInputData,
    viewport: recorderClient.viewport,
  } as const;
};
